import React from "react";
import {
  ArrowLeft,
  Check,
  Loader2,
  Square,
  SquareCheckBig,
  SquareMinus,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { formatter } from "~/functions";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import { BalanceBox, BalanceLine } from "../../components/BalanceLines";

function safeDate(value) {
  if (!value) return "-";
  try {
    return format(parseISO(value), "MM/dd/yyyy");
  } catch (err) {
    return "-";
  }
}

export function rowName(row, type) {
  if (type === "Payable") {
    return row.payee?.issuer?.merchant?.name || row.name || "-";
  }
  const student =
    row.receivable?.issuer?.student ||
    row.settlement?.receivable?.issuer?.student;
  if (student) {
    return `${student.name || ""} ${student.last_name || ""}`.trim() || "-";
  }
  return row.name || "-";
}

export function rowInvoice(row, type) {
  if (type === "Payable") {
    return row.payee?.invoice_number || row.invoice_number || "-";
  }
  return row.receivable?.invoice_number || row.invoice_number || "-";
}

export default function Step2Selection({
  available,
  selectedIds,
  onToggle,
  onToggleAll,
  type,
  expected,
  creating,
  onBack,
  onCreate,
}) {
  const selectedAmount = available
    .filter((row) => selectedIds.includes(row.id))
    .reduce((total, row) => total + Number(row.amount || 0), 0);
  const hasExpected = Math.abs(Number(expected || 0)) >= 0.005;
  const remaining = expected - selectedAmount;
  const isZero = Math.abs(remaining) < 0.005;

  const allSelected =
    available.length > 0 && selectedIds.length === available.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  return (
    <InputLineGroup title="SELECTION" activeMenu>
      <InputLine title="Balance">
        <div className="w-full pt-2">
          <BalanceBox>
            {hasExpected ? (
              <BalanceLine title="Expected" value={expected} />
            ) : null}
            <BalanceLine title="Selected" value={selectedAmount} />
            {hasExpected ? (
              <BalanceLine
                title="Remaining"
                value={remaining}
                tone={isZero ? "success" : "danger"}
              />
            ) : null}
            <div className="w-full flex flex-row items-center justify-between text-gray-500 pt-2">
              <span>
                {selectedIds.length}/{available.length} selected
              </span>
              {hasExpected && isZero && selectedIds.length > 0 ? (
                <span className="flex flex-row items-center gap-1 text-emerald-600">
                  <Check size={14} /> Matches expected
                </span>
              ) : null}
            </div>
          </BalanceBox>
        </div>
      </InputLine>

      <table className="w-full text-xs">
        <thead className="sticky top-0 border-b">
          <tr className="bg-secondary h-8">
            <th className="w-8">
              <div className="flex flex-row items-center justify-center">
                <button
                  type="button"
                  title={allSelected ? "Unselect all" : "Select all"}
                  disabled={available.length === 0}
                  onClick={onToggleAll}
                  className="flex disabled:opacity-40"
                >
                  {allSelected ? (
                    <SquareCheckBig size={14} className="text-emerald-600" />
                  ) : someSelected ? (
                    <SquareMinus size={14} className="text-emerald-600" />
                  ) : (
                    <Square size={14} className="text-gray-400" />
                  )}
                </button>
              </div>
            </th>
            <th>Date</th>
            <th>Name</th>
            <th>Invoice</th>
            <th>Payment Method</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody className="align-center">
          {available.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-center text-gray-500 py-8">
                No processes found for this filter.
              </td>
            </tr>
          ) : (
            available.map((row) => {
              const checked = selectedIds.includes(row.id);
              return (
                <tr
                  key={row.id}
                  onClick={() => onToggle(row.id)}
                  className={`border-b cursor-pointer ${
                    checked ? "bg-emerald-50 text-emerald-700" : "hover:bg-gray-50"
                  }`}
                >
                  <td className="py-1">
                    <div className="flex flex-row items-center justify-center">
                      {checked ? (
                        <SquareCheckBig size={14} className="text-emerald-600" />
                      ) : (
                        <Square size={14} className="text-gray-400" />
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-row items-center justify-center">
                      {safeDate(row.settlement_date)}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-row items-center justify-center">
                      {rowName(row, type)}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-row items-center justify-center">
                      {rowInvoice(row, type)}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-row items-center justify-center">
                      {row.paymentMethod?.description || "-"}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-row items-center justify-center font-bold">
                      {formatter.format(Number(row.amount || 0))}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <InputLine>
        <div className="flex flex-row items-center justify-end gap-2 w-full">
          <button
            type="button"
            onClick={onBack}
            className="border rounded-md p-1 px-2 h-8 flex flex-row items-center justify-center text-xs gap-1 hover:border-primary transition-all"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <button
            type="button"
            disabled={creating}
            onClick={onCreate}
            className="bg-primary text-white rounded-md p-1 px-2 h-8 flex flex-row items-center justify-center text-xs gap-1 hover:opacity-80 disabled:opacity-50 transition-all"
          >
            {creating ? <Loader2 size={16} className="animate-spin" /> : null}
            Create batch
          </button>
        </div>
      </InputLine>
    </InputLineGroup>
  );
}
